import {
    AbstractControl,
    FormArray,
    ValidationErrors,
    ValidatorFn,
    FormGroup,
    FormControl,
} from '@angular/forms';
import { MinimumRadiusValue, UnitOfMeasurement } from '@core/models/common/unit-of-measurement';
import { FORMAT } from '@core/utils/formats';
import { isNullOrUndefined } from '@core/utils/object';
import { isString, isValidEmail } from '@core/utils/string';
import { Moment } from 'moment';
import * as moment from 'moment';
import { extendMoment } from 'moment-range';
import { DateRange } from 'moment-range';


function isEmptyInputValue(value: any): boolean {
    if (typeof value === 'string' || value instanceof String) {
        // remove HTML tags (if any) and trim whitespaces
        const sanitizedValue = (value as string)
            .replace(/<[^>]+>/gm, '')
            .trim();
        return sanitizedValue.length === 0;
    } else {
        // we don't check for string here so it also works with arrays
        return value == null || value.length === 0;
    }
}

export function validNumber(excludeNegative: boolean = false): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const value: any = control.value;
        let isValid: boolean = true;

        if (value) {
            let pattern: RegExp = /^[-\d]+$/;
            if (excludeNegative) {
                pattern = /^[\d]+$/;
            }

            isValid = pattern.test(value);
        }

        return isValid
            ? null
            : {
                  validNumber: false,
              };
    };
}

export function validDecimal(excludeNegative: boolean = false): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const value: any = control.value;
        let isValid: boolean = true;

        if (value) {
            let pattern: RegExp = /^[-.\d]+$/;
            if (excludeNegative) {
                pattern = /^[.\d]+$/;
            }

            isValid = pattern.test(value);
        }

        return isValid
            ? null
            : {
                  validDecimal: false,
              };
    };
}

export function minDistance(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const form: any = control.root;

        if (form) {
            switch (form.value.ci_radius_unit) {
                case UnitOfMeasurement.METERS:
                    if (
                        form.value.ci_radius_value < MinimumRadiusValue.METERS
                    ) {
                        return { minDistance: false };
                    } else {
                        return null;
                    }
                case UnitOfMeasurement.FEET:
                    if (form.value.ci_radius_value < MinimumRadiusValue.FEET) {
                        return { minDistance: false };
                    } else {
                        return null;
                    }
                case UnitOfMeasurement.YARDS:
                    if (form.value.ci_radius_value < MinimumRadiusValue.YARDS) {
                        return { minDistance: false };
                    } else {
                        return null;
                    }
                default:
                    return null;
            }
        }
    };
}

export function requiredNoWhitespace(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const value: any = control.value;

        const isWhitespace = value !== '' && value !== null && (value || '').trim().length === 0;
        if (isWhitespace) {
            return { isWhitespace };
        }

        return null;
    };
}

export function requiredUnique(previousValue: any[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const value: any = control.value;
        let exist: boolean;

        if (previousValue) {
            exist = previousValue.includes(value);
            if (exist) {
                return { notUnique: true };
            } else {
                return null;
            }
        }

        return null;
    };
}

/**
 * Validator that requires at least one position to be selected.
 *
 * @static
 * @returns {ValidatorFn}
 * @memberof OSSValidators
 */
export function requiredArray(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const arrayItems: any = control.value;
        if (arrayItems && arrayItems.length) {
            return null;
        } else {
            return { validArray: { valid: false } };
        }
    };
}

export function maxDailyOvertime(maxValue: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (isEmptyInputValue(control.value) || isEmptyInputValue(maxValue)) {
            return null; // don't validate empty values to allow optional controls
        }

        const value = parseFloat(control.get('after_hours').value);
        // Controls with NaN values after parsing should be treated as not having a
        // maximum, per the HTML forms spec: https://www.w3.org/TR/html5/forms.html#attr-input-max
        return !isNaN(value) && value > maxValue
            ? { max: { max: maxValue, actual: control.value } }
            : null;
    };
}

/**
 * Converts the form group into an array of date ranges.
 * @param {AbstractControl} control
 * @returns {DateRange[]}
 */
function convertFormGroupToRanges(control: AbstractControl): DateRange[] {
    const momentExtended = extendMoment(moment);

    let nowMoment: Moment = moment();

    let formItem = control as FormGroup;
    let formItemDays = formItem.get('days').value;
    let formItemStart = formItem.get('start').value;
    let formItemEnd = formItem.get('end').value;
    let formItemStartMoment: Moment;
    let formItemEndMoment: Moment;
    let formItemRanges: DateRange[] = [];

    // Parse all date ranges
    formItemDays.forEach((dayIndex) => {
        // Parse start
        formItemStartMoment = moment(nowMoment)
            .day(dayIndex)
            .hour(parseInt(formItemStart.substr(0, 2), 10))
            .minute(parseInt(formItemStart.substr(3, 2), 10))
            .seconds(0);

        // Parse end, subtract a second to allow for adjacent ranges to not overlap
        formItemEndMoment = moment(formItemStartMoment)
            .hour(parseInt(formItemEnd.substr(0, 2), 10))
            .minute(parseInt(formItemEnd.substr(3, 2), 10))
            .seconds(0)
            .subtract(1, 'second');

        formItemRanges.push(
            momentExtended.range(formItemStartMoment, formItemEndMoment)
        );
    });

    return formItemRanges;
}

export class OSSValidators {
    /**
     * Validates that the start time is before the end time.
     * @returns {ValidatorFn}
     */
    static invalidTimeRange(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (isEmptyInputValue(control.value)) {
                return null; // don't validate empty values to allow optional controls
            }

            // Convert this formGroup into date ranges
            let formItemRanges: DateRange[] = convertFormGroupToRanges(control);

            // Compare if any range is invalid
            let isInvalid: boolean = formItemRanges.some((range) => {
                return range.start.isSameOrAfter(range.end);
            });

            return isInvalid ? { invalidTimeRange: true } : null;
        };
    }

    /**
     * Validates there are no overlapping penalty hour ranges across the formArray
     * @param {boolean} validateSiblings
     * @returns {ValidatorFn}
     */
    static duplicateRange(validateSiblings: boolean = true): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (isEmptyInputValue(control.value)) {
                return null; // don't validate empty values to allow optional controls
            }

            // Convert this formGroup into date ranges
            let formItem: FormGroup = control as FormGroup;
            let formItemRanges: DateRange[] = convertFormGroupToRanges(control);

            let hasIntersection: boolean = false;
            let formItems: FormArray = formItem.parent as FormArray;
            if (formItems && formItems.length) {
                // Validate there are no intersecting ranges
                let compareFormItem: AbstractControl;
                let compareFormItemRanges: DateRange[];
                let invalidItems: AbstractControl[] = [];
                for (let i = 0, m = formItems.length; i < m; i++) {
                    compareFormItem = formItems.at(i);
                    if (compareFormItem !== control) {
                        compareFormItemRanges = convertFormGroupToRanges(
                            compareFormItem
                        );
                        hasIntersection = !isNullOrUndefined(
                            compareFormItemRanges.find((compareRange) => {
                                let hasFound = formItemRanges.find(
                                    (formItemRange) =>
                                        compareRange.overlaps(formItemRange)
                                );
                                return !!hasFound;
                            })
                        );

                        if (hasIntersection) {
                            invalidItems.push(compareFormItem);
                        } else if (validateSiblings) {
                            // No intersection with current form item so re-validate this formGroup as it may still intersect another
                            // But don't do this recursively with its siblings..
                            let hasErrors = OSSValidators.duplicateRange(false)(
                                compareFormItem
                            );
                            if (isNullOrUndefined(hasErrors)) {
                                if (
                                    compareFormItem.errors &&
                                    compareFormItem.errors.hasOwnProperty(
                                        'duplicateRange'
                                    )
                                ) {
                                    delete compareFormItem.errors
                                        .duplicateRange;
                                    if (
                                        Object.keys(compareFormItem.errors)
                                            .length === 0
                                    ) {
                                        compareFormItem.setErrors(null);
                                    }
                                }
                            }
                        }
                    }
                }

                hasIntersection = invalidItems && invalidItems.length > 0;
                if (hasIntersection) {
                    invalidItems.forEach((invalidFormItem) => {
                        if (!invalidFormItem.hasError('duplicateRange')) {
                            invalidFormItem.setErrors({ duplicateRange: true });
                        }
                    });
                }
            }

            return hasIntersection ? { duplicateRange: true } : null;
        };
    }

    /**
     * Validates there are no duplicate overtime values.
     * @param {boolean} validateSiblings
     * @returns {ValidatorFn}
     */
    static duplicateOvertime(validateSiblings: boolean = true): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (isEmptyInputValue(control.value)) {
                return null; // don't validate empty values to allow optional controls
            }

            let key: string = 'after_minutes';
            let formItem = control as FormGroup;

            // Use latest value from hours to be used for comparison
            let formItemValue: any = formItem.get(key).value;
            let minsControl: AbstractControl = formItem.get('after_minutes');
            let hoursControl: AbstractControl = formItem.get('after_hours');
            if (minsControl.value !== hoursControl.value * 60) {
                formItemValue = hoursControl.value * 60;
            }

            let hasDuplicate: boolean = false;
            let formItems: FormArray = formItem.parent as FormArray;
            if (formItems) {
                // Include both daily and weekly overtimes
                let formControls: AbstractControl[] = formItems.controls;

                const rules: FormGroup = formItems.parent as FormGroup;

                if (rules && rules.controls) {
                    if (formItem.get('period').value === 'week') {
                        const formArray = rules.get(
                            'daily_overtime'
                        ) as FormArray;

                        if (formArray && formArray.controls) {
                            formControls = formControls.concat(
                                formArray.controls
                            );
                        }
                    } else {
                        const formArray = rules.get(
                            'period_overtime'
                        ) as FormArray;

                        if (formArray && formArray.controls) {
                            formControls = formControls.concat(
                                formArray.controls
                            );
                        }
                    }
                }

                if (formControls && formControls.length) {
                    // Validate there are no duplicate values
                    let compareFormItem: AbstractControl;
                    let compareFormItemValue: any;
                    let invalidItems: AbstractControl[] = [];
                    for (let i = 0, m = formControls.length; i < m; i++) {
                        compareFormItem = formControls[i];
                        if (compareFormItem !== control) {
                            compareFormItemValue = compareFormItem.get(key)
                                .value;
                            hasDuplicate =
                                compareFormItemValue === formItemValue;

                            if (hasDuplicate) {
                                invalidItems.push(compareFormItem);
                            } else if (validateSiblings) {
                                // No duplicate with current form item so re-validate this formGroup as it may still duplicate another
                                // But don't do this recursively with its siblings..
                                let hasErrors = OSSValidators.duplicateOvertime(
                                    false
                                )(compareFormItem);
                                if (isNullOrUndefined(hasErrors)) {
                                    if (
                                        compareFormItem.errors &&
                                        compareFormItem.errors.hasOwnProperty(
                                            'duplicateOvertime'
                                        )
                                    ) {
                                        delete compareFormItem.errors
                                            .duplicateOvertime;
                                        if (
                                            Object.keys(compareFormItem.errors)
                                                .length === 0
                                        ) {
                                            compareFormItem.setErrors(null);
                                        }
                                    }
                                }
                            }
                        }
                    }

                    hasDuplicate = invalidItems && invalidItems.length > 0;
                    if (hasDuplicate) {
                        invalidItems.forEach((invalidFormItem) => {
                            if (
                                !invalidFormItem.hasError('duplicateOvertime')
                            ) {
                                invalidFormItem.setErrors({
                                    duplicateOvertime: true,
                                });
                            }
                        });
                    }
                }
            }

            return hasDuplicate ? { duplicateOvertime: true } : null;
        };
    }

    /**
     * Validator that requires allowances controls to have a valid label, cost and code.
     *
     * @static
     * @returns {ValidatorFn}
     * @memberof OSSValidators
     */
    static validAllowance(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const allowance: any = control.value;

            if (
                !allowance ||
                allowance.label === null ||
                allowance.label === undefined ||
                allowance.cost === null ||
                allowance.code === null
            ) {
                return { emptyAllowance: { valid: false } };
            } else {
                let valid: boolean =
                    allowance.label !== null &&
                    allowance.label.length > 0 &&
                    allowance.code !== null &&
                    allowance.code.length > 0 &&
                    allowance.cost !== null &&
                    !isNaN(allowance.cost);

                if (valid) {
                    return null;
                } else {
                    return { validAllowance: { valid: false } };
                }
            }
        };
    }

    static validGroups(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const group: any = control.value;
            if (!group.groupType) {
                return { groupTypeRequired: true };
            }
            if (group.groupType && group.groupType === 'worker') {
                return null;
            } else if (group.groups && group.groups.length) {
                return null;
            }
            return { groupsRequired: true };
        };
    }

    static validTimecardQuestion(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value: any = control.value;

            let isValid: boolean = true;

            if (value && value.length) {
                isValid = !value.some((question) =>
                    isNullOrUndefined(question.answer)
                );
            }

            return isValid
                ? null
                : {
                      validCustomQuestions: false,
                  };
        };
    }

    static validDate(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const dateVal: any = control.value;

            if (dateVal && dateVal.year && dateVal.month && dateVal.day) {
                const shortDate: string = `${dateVal.year}-${dateVal.month}-${dateVal.day}`;
                if (moment(shortDate, FORMAT.DATE_SHORT).isValid()) {
                    return null;
                }
            }
            return { invalid: true };
        };
    }

    static validComplianceRulesetBreakRange(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const ruleBreak: any = control.value;

            const valid: boolean =
                (ruleBreak.from >= 0 && !ruleBreak.to) ||
                ruleBreak.from < ruleBreak.to;
            if (valid) {
                return null;
            } else {
                return { validBreakRange: { valid: false } };
            }
        };
    }

    static validComplianceRulesetBreaks(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const paidBreaks: any[] = control.get('paid').value;
            const unpaidBreaks: any[] = control.get('unpaid').value;

            let validPaidBreaksList: boolean[] = paidBreaks.map(() => true);
            let validUnpaidBreaksList: boolean[] = unpaidBreaks.map(() => true);

            if (paidBreaks && paidBreaks.length > 1) {
                paidBreaks.forEach((paidBreak, index) => {
                    if (index > 0) {
                        // Check against previous breaks
                        if (paidBreak.from < paidBreaks[index - 1].from) {
                            const itemControl: AbstractControl = (control.get(
                                'paid'
                            ) as FormArray).at(index);
                            itemControl.setErrors({
                                validBreakDurationSequence: { valid: false },
                            });
                            itemControl.markAsDirty();
                            validPaidBreaksList[index] = false;
                        } else {
                            (control.get('paid') as FormArray)
                                .at(index)
                                .setErrors(null);
                            validPaidBreaksList[index] = true;
                        }
                    }
                });
            }

            if (unpaidBreaks && unpaidBreaks.length > 1) {
                unpaidBreaks.forEach((unpaidBreak, index) => {
                    if (index > 0) {
                        // Check against previous breaks
                        if (unpaidBreak.from < unpaidBreaks[index - 1].from) {
                            const itemControl: AbstractControl = (control.get(
                                'unpaid'
                            ) as FormArray).at(index);
                            itemControl.setErrors({
                                validBreakDurationSequence: { valid: false },
                            });
                            itemControl.markAsDirty();
                            validUnpaidBreaksList[index] = false;
                        } else {
                            (control.get('unpaid') as FormArray)
                                .at(index)
                                .setErrors(null);
                            validUnpaidBreaksList[index] = true;
                        }
                    }
                });
            }

            const valid: boolean =
                validPaidBreaksList.every((isValid) => isValid) &&
                validUnpaidBreaksList.every((isValid) => isValid);
            if (valid) {
                return null;
            } else {
                return { validBreakDurationSequence: { valid: false } };
            }
        };
    }

    static required(errorName: string = 'required'): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            return isEmptyInputValue(control.value)
                ? { [errorName]: true }
                : null;
        };
    }

    static email(errorName: string = 'email'): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            return isValidEmail(control.value) ? null : { [errorName]: true };
        };
    }

    static unique(
        controlName: string,
        exclusive: boolean,
        previousValue: any,
        errorName: string = 'unique'
    ): ValidatorFn {
        function clearErrors(
            name: string,
            controls: { [key: string]: AbstractControl }
        ) {
            Object.entries(controls).map(
                ([itemName, itemControl]: [string, AbstractControl]) => {
                    if (
                        Object.keys(previousValue).includes(itemName) &&
                        itemControl.hasError(name)
                    ) {
                        itemControl.setErrors(null);
                    }
                }
            );
        }

        function setError(
            name: string,
            controls: { [key: string]: AbstractControl }
        ) {
            Object.entries(controls).map(
                ([itemName, itemControl]: [string, AbstractControl]) => {
                    if (
                        Object.keys(previousValue).includes(itemName) &&
                        itemControl.valid
                    ) {
                        itemControl.setErrors({ [name]: true });
                    }
                }
            );
        }

        return (control: AbstractControl): ValidationErrors | null => {
            if (isNullOrUndefined(previousValue)) {
                return null;
            } else if (exclusive) {
                return control.value !== previousValue
                    ? null
                    : { [errorName]: { valid: false } };
            } else {
                const isValid = control.value !== previousValue[controlName];
                const formItem: FormGroup = control as FormGroup;
                const formItemParent: FormGroup = formItem.parent as FormGroup;

                if (formItemParent) {
                    // Get rule_groups control
                    const ruleGroupsControl = formItemParent.get('rule_groups');

                    if (isValid) {
                        clearErrors(errorName, formItemParent.controls);
                        return null;
                    } else {
                        const validControls = Object.entries(
                            formItemParent.controls
                        ).filter(([name, item]: [string, AbstractControl]) => {
                            return (
                                name !== controlName &&
                                item.value !== previousValue[name]
                            );
                        });

                        if (validControls.length > 0) {
                            clearErrors(errorName, formItemParent.controls);
                            return null;
                        } else {
                            setError(errorName, formItemParent.controls);
                            return { [errorName]: { valid: false } };
                        }
                    }
                }
            }
        };
    }

    /**
     * Validates a group of linked fields, at least one of these must be defined (OR).
     * Defaults to first_name, last_name.
     * @param {string[]} fields
     * @param {string} errorName Support to overload the error name, allows for multiple groupings.
     * @returns {ValidatorFn}
     */
    static requiredMany(
        requiredFields: string[] = ['first_name', 'last_name'],
        errorName: string = 'requiredMany'
    ): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const formItem: FormGroup = control as FormGroup;
            const formItemParent: FormGroup = formItem.parent as FormGroup;
            if (formItemParent) {
                let isValid: boolean = false;
                const controls: FormControl[] = Object.entries(
                    formItemParent.controls
                )
                    .filter(([controlName]) =>
                        requiredFields.includes(controlName)
                    )
                    .map(
                        ([controlName, controlItem]: [
                            string,
                            AbstractControl
                        ]) => {
                            // Validate all the matching control values are present
                            let requiredControl: FormControl = controlItem as FormControl;
                            if (!isValid && requiredControl) {
                                isValid =
                                    isString(requiredControl.value) &&
                                    requiredControl.value.trim().length > 0;
                            }

                            return requiredControl;
                        }
                    );

                if (isValid) {
                    controls.forEach((requiredControl) =>
                        requiredControl.setErrors(null)
                    );
                    return null;
                } else {
                    controls.forEach((requiredControl) =>
                        requiredControl.setErrors({ [errorName]: true })
                    );
                    return { [errorName]: true };
                }
            }
        };
    }

    static requiredGoogleAddress(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const address: any = control.value;
            if (
                address &&
                (address.goo_place_id ||
                    (address.selected && address.selected.googlePlaceId))
            ) {
                return null;
            } else {
                return { address: { valid: false } };
            }
        };
    }
}
